import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import api from '../services/api';
import VoiceNoteButton from '../components/VoiceNoteButton';

export default function ResumeAssessment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('loading'); // loading, intro, questions, generating, report
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({ q1Target: '', q2Work1: '', q2Work2: '', q3Skills: '', q4Initiative: '', q5Worry: '' });
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [resumeType, setResumeType] = useState('');

  useEffect(() => {
    loadExisting();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadExisting() {
    try {
      const { data } = await api.get(`/resume/${id}/assessment`);
      if (data.status === 'completed') {
        setReport(data);
        setStep('report');
      } else {
        setStep('intro');
      }
    } catch {
      setStep('intro');
    }
  }

  async function startAssessment() {
    setError('');
    try {
      const { data } = await api.post(`/resume/${id}/assessment/start`);
      setQuestions(data.questions || []);
      setResumeType(data.resumeType || '');
      setStep('questions');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start assessment.');
    }
  }

  async function submitAnswers() {
    if (!answers.q1Target || !answers.q2Work1 || !answers.q3Skills || !answers.q4Initiative) {
      setError('Please answer all required questions.');
      return;
    }
    setError('');
    setStep('generating');
    try {
      const { data } = await api.post(`/resume/${id}/assessment/submit`, { answers }, { timeout: 120000 });
      setReport(data);
      setStep('report');
    } catch (e) {
      setError(e.response?.data?.error || 'Assessment generation failed. Please try again.');
      setStep('questions');
    }
  }

  async function regenerate() {
    setError('');
    setStep('generating');
    try {
      const { data } = await api.post(`/resume/${id}/assessment/regenerate`, {}, { timeout: 120000 });
      setReport(data);
      setStep('report');
    } catch (e) {
      setError(e.response?.data?.error || 'Regeneration failed.');
      setStep('report');
    }
  }

  function downloadReport() {
    if (!report?.report) return;
    const name = report.firstName || 'Student';
    const date = report.generatedAt ? new Date(report.generatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>AskNitin Assessment Report</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&display=swap');
body { font-family: 'DM Sans', sans-serif; max-width: 700px; margin: 40px auto; padding: 0 32px; color: #1a2f4a; line-height: 1.8; font-size: 15px; }
.header { text-align: center; margin-bottom: 28px; }
.header h1 { font-size: 22px; color: #0c2340; margin: 0 0 4px; }
.header p { font-size: 13px; color: #6b7280; margin: 0; }
.score { text-align: center; font-size: 48px; font-weight: 800; color: #0c2340; margin: 20px 0; }
.score span { font-size: 18px; color: #9ca3af; }
.footer { margin-top: 32px; font-size: 13px; color: #9ca3af; font-style: italic; text-align: center; }
h1,h2,h3 { color: #0c2340; }
@media print { body { margin: 20px; } }
</style></head><body>
<div class="header"><h1>AskNitin Assessment Report</h1><p>${name} | ${date}</p></div>
<div class="score">${report.score}<span>/100</span></div>
${report.report.split('\n').map(l => l.trim() ? '<p>' + l.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>') + '</p>' : '').join('\n')}
<p class="footer">www.asknitin.com</p>
</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 400);
  }

  // ── Loading ──
  if (step === 'loading') {
    return (
      <div style={s.page}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div style={s.spin} />
        </div>
      </div>
    );
  }

  // ── Intro ──
  if (step === 'intro') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <button onClick={() => navigate(`/resume/${id}`)} style={s.backBtn}>← Back to Resume</button>
          <h1 style={s.h1}>Before we assess you, a few quick questions</h1>
          <p style={s.subtitle}>Because a resume alone never tells the full story.</p>
          <p style={s.body}>
            This assessment evaluates your profile across five pillars — academics, internships, skills, initiative, and moots & publications. It scores you out of 100 and delivers a personalised report telling you honestly where you stand, where you can grow, and what path may genuinely suit you.
          </p>
          <p style={s.body}>
            The philosophy is simple: <strong>being a rank holder is one thing; being capable is another.</strong> Your resume helps you present yourself well. This assessment helps you understand yourself honestly.
          </p>
          <div style={s.reassurance}>
            <p style={{ margin: 0, fontStyle: 'italic', color: '#1e3a8a' }}>This is not a judgement. This is a conversation.</p>
          </div>
          {error && <div style={s.error}>{error}</div>}
          <button onClick={startAssessment} style={s.primaryBtn}>Continue</button>
          <p style={s.tokenNote}>This assessment costs 3 tokens.</p>
        </div>
      </div>
    );
  }

  // ── Questions ──
  if (step === 'questions') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <button onClick={() => navigate(`/resume/${id}`)} style={s.backBtn}>← Back to Resume</button>
          <h1 style={s.h1}>A few questions about you</h1>
          <p style={s.subtitle}>Answer honestly. There are no right or wrong answers.</p>

          {/* Q1 — Target */}
          <div style={s.qBlock}>
            <label style={s.qLabel}>1. Where do you see yourself in your first job after law school?</label>
            <div style={s.radioGroup}>
              {[
                { value: 'tier1-firm', label: 'Tier-1 Law Firm (M&A, Banking, Disputes)' },
                { value: 'boutique', label: 'Tier-2 / Boutique Firm' },
                { value: 'litigation', label: 'Litigation Chambers' },
                { value: 'inhouse', label: 'In-House Counsel' },
                { value: 'judiciary', label: 'Judiciary' },
                { value: 'exploring', label: 'Still Exploring' },
              ].map(opt => (
                <label key={opt.value} style={{ ...s.radio, ...(answers.q1Target === opt.value ? s.radioSelected : {}) }}>
                  <input type="radio" name="q1" value={opt.value} checked={answers.q1Target === opt.value} onChange={() => setAnswers(a => ({ ...a, q1Target: opt.value }))} style={{ display: 'none' }} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Q2 — Internship substance */}
          <div style={s.qBlock}>
            <label style={s.qLabel}>2. Across all your internships, describe TWO pieces of work you are most proud of. For each — what was it, what did you actually do, and what was the outcome?</label>
            <p style={s.qHint}>Keep each answer to 3-4 lines.</p>
            <label style={s.subLabel}>Piece of Work 1 *</label>
            <div style={s.textareaWrap}>
              <textarea
                value={answers.q2Work1}
                onChange={e => setAnswers(a => ({ ...a, q2Work1: e.target.value }))}
                placeholder="Describe your first piece of work..."
                maxLength={400}
                rows={4}
                style={s.textarea}
              />
              <div style={s.voiceBtnWrap}>
                <VoiceNoteButton onTranscript={(text) => setAnswers(a => ({ ...a, q2Work1: a.q2Work1 ? a.q2Work1.trimEnd() + ' ' + text : text }))} />
              </div>
            </div>
            <span style={s.charCount}>{answers.q2Work1.length}/400</span>
            <label style={{ ...s.subLabel, marginTop: 12 }}>Piece of Work 2</label>
            <div style={s.textareaWrap}>
              <textarea
                value={answers.q2Work2}
                onChange={e => setAnswers(a => ({ ...a, q2Work2: e.target.value }))}
                placeholder="Describe your second piece of work..."
                maxLength={400}
                rows={4}
                style={s.textarea}
              />
              <div style={s.voiceBtnWrap}>
                <VoiceNoteButton onTranscript={(text) => setAnswers(a => ({ ...a, q2Work2: a.q2Work2 ? a.q2Work2.trimEnd() + ' ' + text : text }))} />
              </div>
            </div>
            <span style={s.charCount}>{answers.q2Work2.length}/400</span>
          </div>

          {/* Q3 — Skills */}
          <div style={s.qBlock}>
            <label style={s.qLabel}>3. Tell us about your real strengths beyond academics — courses, IT skills, soft skills, languages. Give examples, not just labels.</label>
            <div style={s.subPrompts}>
              <p><strong>Courses:</strong> Any certifications (NISM, CS foundation, arbitration, IP, CopyrightX, etc.) that genuinely added to your learning?</p>
              <p><strong>IT & Legal Tech:</strong> Are you comfortable with Manupatra, SCC Online, Westlaw, MS Office, AI legal tools?</p>
              <p><strong>Soft Skills:</strong> Have you spoken at events, drafted independently, moderated panels, written for publications?</p>
              <p><strong>Languages:</strong> Which ones do you speak, read, or write with working proficiency?</p>
            </div>
            <div style={s.textareaWrap}>
              <textarea
                value={answers.q3Skills}
                onChange={e => setAnswers(a => ({ ...a, q3Skills: e.target.value }))}
                placeholder="Describe your skills with examples..."
                maxLength={800}
                rows={6}
                style={s.textarea}
              />
              <div style={s.voiceBtnWrap}>
                <VoiceNoteButton onTranscript={(text) => setAnswers(a => ({ ...a, q3Skills: a.q3Skills ? a.q3Skills.trimEnd() + ' ' + text : text }))} />
              </div>
            </div>
            <span style={s.charCount}>{answers.q3Skills.length}/800</span>
          </div>

          {/* Q4 — Initiative */}
          <div style={s.qBlock}>
            <label style={s.qLabel}>4. Outside of academics and internships, what have you built, started, led, or created on your own?</label>
            <p style={s.qHint}>A blog, a society, an event, a research project, a podcast, a cause — anything.</p>
            <div style={s.textareaWrap}>
              <textarea
                value={answers.q4Initiative}
                onChange={e => setAnswers(a => ({ ...a, q4Initiative: e.target.value }))}
                placeholder="Tell us about your initiatives..."
                maxLength={500}
                rows={4}
                style={s.textarea}
              />
              <div style={s.voiceBtnWrap}>
                <VoiceNoteButton onTranscript={(text) => setAnswers(a => ({ ...a, q4Initiative: a.q4Initiative ? a.q4Initiative.trimEnd() + ' ' + text : text }))} />
              </div>
            </div>
            <span style={s.charCount}>{answers.q4Initiative.length}/500</span>
          </div>

          {/* Q5 — Optional */}
          <div style={s.qBlock}>
            <label style={s.qLabel}>5. What is the ONE thing about your profile you are most worried about? <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional)</span></label>
            <div style={s.textareaWrap}>
              <textarea
                value={answers.q5Worry}
                onChange={e => setAnswers(a => ({ ...a, q5Worry: e.target.value }))}
                placeholder="Share your worry — it helps us address it in the assessment..."
                maxLength={300}
                rows={3}
                style={s.textarea}
              />
              <div style={s.voiceBtnWrap}>
                <VoiceNoteButton onTranscript={(text) => setAnswers(a => ({ ...a, q5Worry: a.q5Worry ? a.q5Worry.trimEnd() + ' ' + text : text }))} />
              </div>
            </div>
            <span style={s.charCount}>{answers.q5Worry.length}/300</span>
          </div>

          {error && <div style={s.error}>{error}</div>}
          <button
            onClick={submitAnswers}
            disabled={!answers.q1Target || !answers.q2Work1 || !answers.q3Skills || !answers.q4Initiative}
            style={{ ...s.primaryBtn, opacity: (!answers.q1Target || !answers.q2Work1 || !answers.q3Skills || !answers.q4Initiative) ? 0.45 : 1 }}
          >
            Get My Assessment
          </button>
        </div>
      </div>
    );
  }

  // ── Generating ──
  if (step === 'generating') {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, textAlign: 'center', padding: '60px 32px' }}>
          <div style={s.spin} />
          <h2 style={{ ...s.h1, fontSize: 20, marginTop: 20 }}>Generating your assessment...</h2>
          <p style={s.body}>We are reading your resume, analysing your answers, and writing a personalised report. This takes about 30 seconds.</p>
        </div>
      </div>
    );
  }

  // ── Report ──
  if (step === 'report' && report) {
    const scoreColor = report.score >= 75 ? '#065f46' : report.score >= 55 ? '#1e40af' : report.score >= 40 ? '#92400e' : '#991b1b';
    const scoreBg = report.score >= 75 ? '#f0fdf4' : report.score >= 55 ? '#eff6ff' : report.score >= 40 ? '#fffbeb' : '#fef2f2';
    const pillars = [
      { key: 'academics', label: 'Academics', max: 15 },
      { key: 'internships', label: 'Internships', max: 25 },
      { key: 'skillsCourses', label: 'Skills & Courses', max: 25 },
      { key: 'initiative', label: 'Initiative & Drive', max: 15 },
      { key: 'mootsPublications', label: 'Moots & Publications', max: 20 },
    ];

    return (
      <div style={s.page}>
        <button onClick={() => navigate(`/resume/${id}`)} style={{ ...s.backBtn, marginBottom: 12 }}>← Back to Resume</button>

        {/* Score card */}
        <div style={{ ...s.card, background: scoreBg, borderColor: scoreColor + '33', textAlign: 'center', marginBottom: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: scoreColor, textTransform: 'uppercase', letterSpacing: 2, margin: '0 0 8px' }}>Where You Stand Today</p>
          <p style={{ fontSize: 52, fontWeight: 800, color: scoreColor, margin: 0, lineHeight: 1 }}>{report.score}<span style={{ fontSize: 20, color: '#9ca3af' }}>/100</span></p>
          {report.practiceAreaFitScore != null && (
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 10 }}>Practice Area Fit: <strong style={{ color: scoreColor }}>{report.practiceAreaFitScore}/100</strong></p>
          )}
        </div>

        {/* Pillar breakdown */}
        <div style={{ ...s.card, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0c2340', margin: '0 0 12px' }}>Pillar Breakdown</h3>
          {pillars.map(p => {
            const score = report.pillarScores?.[p.key] ?? 0;
            const pct = (score / p.max) * 100;
            return (
              <div key={p.key} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  <span style={{ color: '#374151' }}>{p.label}</span>
                  <span style={{ color: '#0c2340' }}>{score}/{p.max}</span>
                </div>
                <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: pct >= 70 ? '#065f46' : pct >= 50 ? '#1e40af' : '#d97706', borderRadius: 3, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Full report */}
        <div style={{ ...s.card, marginBottom: 16 }}>
          {report.generatedAt && (
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 12px' }}>
              Generated {new Date(report.generatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              {report.version > 1 ? ` (v${report.version})` : ''}
            </p>
          )}
          <div style={s.reportBody}>
            <ReactMarkdown
              components={{
                p: ({ children }) => <p style={{ margin: '0 0 10px', lineHeight: 1.75 }}>{children}</p>,
                strong: ({ children }) => <strong style={{ color: '#0c2340' }}>{children}</strong>,
                em: ({ children }) => <em>{children}</em>,
                h1: ({ children }) => <p style={{ fontWeight: 700, fontSize: 17, margin: '16px 0 6px', color: '#0c2340' }}>{children}</p>,
                h2: ({ children }) => <p style={{ fontWeight: 700, fontSize: 15, margin: '14px 0 6px', color: '#0c2340' }}>{children}</p>,
                h3: ({ children }) => <p style={{ fontWeight: 700, fontSize: 14, margin: '12px 0 4px', color: '#0c2340' }}>{children}</p>,
                ul: ({ children }) => <ul style={{ paddingLeft: 18, margin: '6px 0' }}>{children}</ul>,
                li: ({ children }) => <li style={{ lineHeight: 1.7, marginBottom: 4 }}>{children}</li>,
              }}
            >
              {report.report}
            </ReactMarkdown>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(12,35,64,0.4)', margin: '16px 0 0', fontStyle: 'italic' }}>www.asknitin.com</p>
        </div>

        {error && <div style={s.error}>{error}</div>}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={downloadReport} style={s.primaryBtn}>Download Report</button>
          <button onClick={regenerate} style={s.secondaryBtn}>Regenerate (2 tokens)</button>
          <button onClick={() => navigate(`/resume/${id}`)} style={s.secondaryBtn}>Back to Resume</button>
        </div>
      </div>
    );
  }

  return null;
}

const s = {
  page: { maxWidth: '700px', margin: '0 auto', padding: '32px 18px 60px' },
  card: { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '28px 24px', boxShadow: '0 2px 12px rgba(12,35,64,0.05)', marginBottom: 0 },
  spin: { width: 28, height: 28, borderRadius: '50%', border: '2.5px solid #e5e7eb', borderTop: '2.5px solid #0c2340', animation: 'spin 0.8s linear infinite', margin: '0 auto' },
  backBtn: { background: 'none', border: 'none', color: '#0c2340', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 },
  h1: { fontFamily: "'DM Sans',sans-serif", fontSize: 24, fontWeight: 800, color: '#0c2340', margin: '0 0 6px' },
  subtitle: { fontSize: 14, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6, fontStyle: 'italic' },
  body: { fontSize: 14, color: '#374151', lineHeight: 1.7, margin: '0 0 12px' },
  reassurance: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 18px', marginBottom: 16 },
  primaryBtn: { width: '100%', background: '#0c2340', color: '#fff', border: 'none', padding: '13px 24px', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
  secondaryBtn: { background: '#f3f4f6', color: '#0c2340', border: '1px solid #d1d5db', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
  tokenNote: { fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 8 },
  error: { fontSize: 13, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 12 },
  qBlock: { marginBottom: 24 },
  qLabel: { display: 'block', fontSize: 14, fontWeight: 700, color: '#0c2340', marginBottom: 8, lineHeight: 1.5 },
  qHint: { fontSize: 12, color: '#6b7280', margin: '-4px 0 8px', lineHeight: 1.5 },
  subLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  textarea: { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 13, fontFamily: "'DM Sans',sans-serif", resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 },
  textareaWrap: { position: 'relative' },
  voiceBtnWrap: { position: 'absolute', right: 8, bottom: 8, zIndex: 2 },
  charCount: { display: 'block', fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 2 },
  radioGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  radio: { display: 'block', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 13, cursor: 'pointer', color: '#374151', transition: 'all 0.15s' },
  radioSelected: { borderColor: '#1e3a8a', background: '#eff6ff', color: '#1e3a8a', fontWeight: 600 },
  subPrompts: { fontSize: 12, color: '#6b7280', lineHeight: 1.6, marginBottom: 10, background: '#f8fafc', borderRadius: 10, padding: '10px 14px', border: '1px solid #e2e8f0' },
  reportBody: { fontSize: 15, color: '#1a2f4a', lineHeight: 1.75 },
};
